"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowUpRight, CircleCheck as CheckCircle2, Cpu, ShieldAlert, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const techSpecs = [{
  icon: Activity,
  label: "OPERATING CLEARANCE",
  value: "20' × 20' MINIMUM",
  desc: "10 ft clearance overhead from light fixtures and beams"
}, {
  icon: Zap,
  label: "ELECTRICAL SUPPLY",
  value: "220V 1-PHASE / 3-PHASE",
  desc: "Dedicated circuit breaker configuration required"
}, {
  icon: Cpu,
  label: "ACCESS CORRIDOR",
  value: "36\" DOORWAY WIDTH",
  desc: "Flat threshold access required for frame transit"
}];
export default function PlanBullSetup() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 8
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="planbullsetup" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-y border-border">
      <div className="container mx-auto px-4 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="border border-border bg-card p-6 md:p-12 shadow-xs relative">
          {/* Industrial corner registration markers */}
          <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-primary" />
          <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-primary" />
          <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-primary" />
          <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-primary" />

          {/* Section Classification Eyebrow */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs uppercase tracking-wider rounded-none px-2.5 py-1 bg-muted border-border text-foreground">
                SEC-ENG-04 // PRE-PURCHASE STAGE
              </Badge>
              <span className="hidden sm:inline-block font-mono text-xs text-muted-foreground uppercase tracking-wider">
                REV: 2026.1
              </span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground font-mono text-xs">
              <ShieldAlert className="size-3 text-primary" />
              <span className="hidden sm:inline">FACILITY VERIFICATION</span>
            </div>
          </div>

          {/* Main H2 Heading */}
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
            Plan Your Mechanical Bull Setup Before You Buy
          </h2>

          <Separator className="mb-6 bg-border" />

          {/* Body Narrative */}
          <div className="space-y-4 text-base md:text-lg text-foreground font-sans leading-relaxed max-w-4xl">
            <p>
              The best time to confirm space, power, access and package requirements is before the equipment arrives. Compare the exact machine, review its setup information and make sure the complete system fits the location where you intend to use it. If you are considering a specific bull, use its product name or SKU when contacting us so the available product information can be reviewed in context.
            </p>
            <p className="text-muted-foreground text-sm md:text-base">
              Ensure you review all necessary modular components and safety backups in our{" "}
              <Link to="/MechanicalBullPartsAndAccessoriesPage" className="font-medium text-foreground underline underline-offset-4 decoration-primary hover:text-primary transition-colors inline-flex items-center gap-0.5">
                Parts
                <ArrowUpRight className="size-3.5 inline text-primary" />
              </Link>{" "}
              catalog, and cross-reference tier budgets against the published{" "}
              <Link to="/MechanicalBullPriceGuidePage" className="font-medium text-foreground underline underline-offset-4 decoration-primary hover:text-primary transition-colors inline-flex items-center gap-0.5">
                Price Guide
                <ArrowUpRight className="size-3.5 inline text-primary" />
              </Link>{" "}
              to align power draw, freight sizing, and staging logistics accurately.
            </p>
          </div>

          {/* Technical Metal Rating Plate Frame */}
          <div className="my-8 border border-border bg-muted/60 p-4 md:p-6 relative">
            <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="size-2 bg-primary rounded-none" />
                <span className="font-mono text-xs font-semibold uppercase tracking-widest text-foreground">
                  SPECIFICATION CHECKLIST // ON-SITE READINESS
                </span>
              </div>
              <span className="font-mono text-2xs text-muted-foreground hidden sm:block">
                TOLERANCE: CLASS-A HEAVY COMMERCIAL
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {techSpecs.map((spec, i) => {
              const IconComponent = spec.icon;
              return <div key={spec.label} data-index={i} className="border border-border bg-card p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="font-mono text-2xs text-muted-foreground tracking-wider uppercase">
                          {spec.label}
                        </span>
                        <IconComponent className="size-4 text-primary" />
                      </div>
                      <div className="font-mono text-sm md:text-base font-bold text-foreground">
                        {spec.value}
                      </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-border flex items-start gap-1.5 text-xs text-muted-foreground">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                      <span>{spec.desc}</span>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Operational CTA Button Pathways */}
          <div className="pt-2">
            <motion.div variants={containerVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
              <motion.div variants={itemVariants} className="w-full sm:w-auto">
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-none uppercase font-mono tracking-wider text-xs md:text-sm border-border bg-card hover:bg-accent text-foreground transition-[border-color,background-color] hover:border-foreground">
                  <Link to="/NewMechanicalBullsPage">
                    View New Bulls
                  </Link>
                </Button>
              </motion.div>

              <motion.div variants={itemVariants} className="w-full sm:w-auto">
                <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto rounded-none uppercase font-mono tracking-wider text-xs md:text-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-colors">
                  <Link to="/UsedMechanicalBullsPage">
                    View Used Bulls
                  </Link>
                </Button>
              </motion.div>

              <motion.div variants={itemVariants} className="w-full sm:w-auto sm:ml-auto">
                <Button asChild variant="default" size="lg" className="w-full sm:w-auto rounded-none uppercase font-mono tracking-wider text-xs md:text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-none">
                  <Link to="/ContactPage">
                    Contact Us
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}